import React from "react";
import { ScopeSelect } from "./ScopeSelect";
import { Input } from "./ui/input";
import { STAGE_ORDER, STAGE_LABEL, TEMP_LABEL, TYPE_LABEL } from "../data/crm";
import { AddressInput } from "./AddressInput";
import { AutoGrowTextarea } from "./AutoGrowTextarea";
import { Plus, Star, Trash2 } from "lucide-react";
import { toast } from "./ui/sonner";
import { formatPhone, formatPhoneInput, validatePhone, phoneDigits } from "../lib/phone";
import { ReferralPicker } from "./crm/ReferralPicker";
import { REFERRAL_SOURCES } from "./referral/refUtils";

/* 新增客户线索 / 编辑客户信息 共用的表单字段（布局与内容完全一致），编辑时额外挂「其他联系人」 */
export const inp = "w-full text-sm rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-700 focus:border-indigo-400 focus:outline-none";
/* 来源下拉里「其他（手动填写）」的哨兵值：只存在于下拉的 value 上，永远不会写进 leadSource ——
   选中它时立刻把 leadSource 清空并展开手填框，手填的原文才是最终入库的来源名。 */
export const SOURCE_OTHER = "__other__";
const Lbl = ({ children }) => <label className="text-xs text-slate-500">{children}</label>;
const REGIONS = ["la", "norcal", "sd"];
/* 与后端 SERVICE_TYPES 一致的兜底：/crm/meta 尚未返回或请求失败时，客户类型仍可选择 */
export const SERVICE_TYPES_FALLBACK = [
  { key: "design_build", zh: "设计施工一站式服务", en: "Design-Build (Full Service)" },
  { key: "permit_only", zh: "Permit 办理（已有 Ready-for-Permit 图纸）", en: "Permit Only (Ready-for-Permit)" },
  { key: "design_only", zh: "仅建筑设计", en: "Design Only" },
];

export const emptyLeadForm = (meta, selfName = "") => ({ firstName: "", lastName: "", phone: "", email: "", address: "", city: "",
  region: "", branch: "", serviceType: "design_build", leadSource: "", projectType: "detached_adu",
  budget: "", stage: "new", temperature: "warm", assignedSales: selfName,
  usDesignOwner: "", cnDraftingOwner: "", referral: null,
  designRatePerSqft: meta?.designDefaultRate ?? 10, designArea: "", manualDesignFee: "", designQuoteChoice: "area",
  desiredStart: "", ownerLang: "zh", primaryConcern: "" });

/* 已有线索 → 表单：姓名拆成 名 / 姓（无记录时按最后一个空格拆），预算 $ → 万美元 */
export const leadFormFromLead = (lead, meta) => {
  const name = (lead.clientName || "").trim();
  const sp = name.lastIndexOf(" ");
  const stored = `${lead.firstName || ""} ${lead.lastName || ""}`.trim();
  const useStored = stored && (!name || stored === name);
  const first = useStored ? (lead.firstName || "") : (sp > 0 ? name.slice(0, sp) : name);
  const last = useStored ? (lead.lastName || "") : (sp > 0 ? name.slice(sp + 1) : "");
  const branch = lead.branch || (REGIONS.includes(lead.region) ? lead.region : "");
  return { ...emptyLeadForm(meta), firstName: first, lastName: last, phone: lead.phone || "", email: lead.email || "", address: lead.address || "", city: lead.city || "",
    region: lead.region || (REGIONS.includes(branch) ? branch : ""), branch, serviceType: lead.serviceType || "design_build", leadSource: lead.leadSource || "",
    projectType: lead.projectType || "detached_adu", budget: lead.budget ? String(Math.round((Number(lead.budget) / 10000) * 100) / 100) : "",
    stage: lead.stage || "new", temperature: lead.temperature || "warm", assignedSales: lead.assignedSales || "", usDesignOwner: lead.usDesignOwner || "", cnDraftingOwner: lead.cnDraftingOwner || "",
    referral: lead.referral?.partnerId ? { partnerId: lead.referral.partnerId, name: lead.referral.partnerName, phone: lead.referral.phone, email: lead.referral.email, company: lead.referral.company } : null,
    designRatePerSqft: lead.designRatePerSqft ?? (meta?.designDefaultRate ?? 10), designArea: lead.designArea || "", manualDesignFee: lead.manualDesignFee || "", designQuoteChoice: lead.designQuoteChoice || "area",
    desiredStart: (lead.desiredStart || "").slice(0, 10), ownerLang: lead.ownerLang || "zh", primaryConcern: lead.primaryConcern || "" };
};

/* 表单 → API body（新增 / 编辑通用）
 * SYS-001：提交前把主电话与各联系人电话统一成 NNN-NNN-NNNN；后端还会再校验 + 查重一次。 */
export const leadBodyFromForm = (form) => ({ ...form, phone: formatPhone(form.phone), clientName: `${form.firstName} ${form.lastName}`.trim(),
  budget: Math.round((Number(form.budget) || 0) * 10000),
  referral: REFERRAL_SOURCES[form.leadSource] ? (form.referral || {}) : undefined,
  designRatePerSqft: Number(form.designRatePerSqft) || 0, designArea: Number(form.designArea) || 0, manualDesignFee: Number(form.manualDesignFee) || 0 });

/**
 * 保存前的本地电话校验（SYS-001）：
 *  - 主电话格式不合法 → 返回错误文案；
 *  - 主电话与联系人电话重复，或联系人之间重复 → 返回错误文案（同一客户只留一处）。
 * 返回空串表示通过；真正的跨客户查重由后端兜底（前端只拦明显问题，避免多一次请求）。
 */
export const validateLeadPhones = (form, contacts = [], zh = true) => {
  const mainErr = validatePhone(form?.phone, zh);
  if (mainErr) return mainErr;
  const seen = new Map();
  if (phoneDigits(form?.phone)) seen.set(phoneDigits(form.phone), zh ? "主电话" : "primary");
  for (const c of contacts || []) {
    const err = validatePhone(c?.phone, zh);
    if (err) return `${c?.name || (zh ? "联系人" : "Contact")}：${err}`;
    const d = phoneDigits(c?.phone);
    if (!d) continue;
    if (seen.has(d)) return zh ? `电话 ${formatPhone(c.phone)} 与「${seen.get(d)}」重复，请只保留一处` : `Phone ${formatPhone(c.phone)} duplicates "${seen.get(d)}"`;
    seen.set(d, c?.name || (zh ? "联系人" : "Contact"));
  }
  return "";
};

export function LeadFields({ form, setForm, meta, zh, tid = "lead", lockSales = false, selfName = "" }) {
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const owners = meta?.designOwners || {};
  const areaQuote = (Number(form.designRatePerSqft) || 0) * (Number(form.designArea) || 0);
  const finalDesignQuote = form.designQuoteChoice === "manual" ? (Number(form.manualDesignFee) || 0) : areaQuote;
  /* 来源下拉：选项由各分公司在「营销来源类别管理」里自行维护，这里额外固定提供「其他（手动填写）」，
     选中后展开手填框，把填写的名称**直接作为 leadSource 存库**（需求 FB56c9f124a7 / 2026-10-15，
     提交人 Jeremy Zhang：来源不在列表里时只能随便选一个，需要一个 Other 兜底并能写明具体来源）。 */
  const sourceList = ((meta?.sourcesByRegion && form.region ? meta.sourcesByRegion[form.region] : meta?.sources) || []);
  const [otherMode, setOtherMode] = React.useState(false);
  const [otherText, setOtherText] = React.useState("");
  /* 换分公司 / 改地址会连带把 region 改掉并清空 leadSource → 手填态同步复位，
     否则会残留上一个分公司的输入，看着像「选了其他却没生效」。 */
  React.useEffect(() => { setOtherMode(false); setOtherText(""); }, [form.region]);
  const pickSource = (v) => {
    if (v === SOURCE_OTHER) { setOtherMode(true); setOtherText(""); set("leadSource", ""); return; }
    setOtherMode(false); setOtherText(""); set("leadSource", v);
  };
  const typeSource = (v) => { setOtherText(v); set("leadSource", v.trim()); };
  return (
    <div className="grid grid-cols-2 gap-3" data-testid={`${tid}-fields`}>
      <div><Lbl>{zh ? "名 First *" : "First *"}</Lbl><Input data-testid={`${tid}-first`} value={form.firstName} onChange={(e) => set("firstName", e.target.value)} /></div>
      <div><Lbl>{zh ? "姓 Last" : "Last"}</Lbl><Input data-testid={`${tid}-last`} value={form.lastName} onChange={(e) => set("lastName", e.target.value)} /></div>
      {/* SYS-001：电话输入时自动按 NNN-NNN-NNNN 分段，失焦再统一格式；
          提示文案写明格式要求，避免再出现 626.227.8888 / 6262278888 混用。 */}
      <div><Lbl>{zh ? "电话" : "Phone"}</Lbl>
        <Input data-testid={`${tid}-phone`} value={form.phone} inputMode="tel" placeholder="626-227-8888"
          onChange={(e) => set("phone", formatPhoneInput(e.target.value))}
          onBlur={(e) => set("phone", formatPhone(e.target.value))} />
        <p className="text-[10px] text-slate-400 mt-0.5">{zh ? "统一为 626-227-8888 格式；每位客户只保留这一个主电话" : "Stored as 626-227-8888; one primary phone per customer"}</p>
      </div>
      <div><Lbl>{zh ? "邮箱" : "Email"}</Lbl><Input data-testid={`${tid}-email`} value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
      <div className="col-span-2"><Lbl>{zh ? "项目地址" : "Address"}</Lbl>
        <AddressInput testid={`${tid}-address`} value={form.address} onChange={(v) => set("address", v)} placeholder="123 Main St, San Jose, CA 95112"
          onSelect={(d) => setForm((s) => {
            const branch = d.branch && (meta?.branches || []).some((b) => b.id === d.branch) ? d.branch : s.branch;
            return { ...s, address: d.formattedAddress || s.address, city: d.city || s.city, branch,
              region: REGIONS.includes(branch) ? branch : s.region, leadSource: branch !== s.branch ? "" : s.leadSource };
          })} />
        {form.city && <p className="text-[11px] text-slate-400 mt-1" data-testid={`${tid}-city-hint`}>{zh ? "城市：" : "City: "}{form.city}</p>}
      </div>
      <div><Lbl>{zh ? "所属分公司（含区域）*" : "Branch (region) *"}</Lbl>
        <ScopeSelect kind="region" className={inp} data-testid={`${tid}-branch`} value={form.branch}
          onChange={(e) => { const v = e.target.value; setForm((s) => (s.branch === v ? s : { ...s, branch: v, region: REGIONS.includes(v) ? v : "", leadSource: "" })); }}>
          <option value="">{zh ? "选择分公司" : "Select branch"}</option>
          {(meta?.branches || []).map((b) => <option key={b.id} value={b.id}>{zh ? b.zh : b.en}</option>)}
        </ScopeSelect>
      </div>
      <div><Lbl>{zh ? "客户类型 *" : "Client Type *"}</Lbl>
        <select className={inp} data-testid={`${tid}-service-type`} value={form.serviceType} onChange={(e) => set("serviceType", e.target.value)}>
          {(meta?.serviceTypes?.length ? meta.serviceTypes : SERVICE_TYPES_FALLBACK).map((s) => <option key={s.key} value={s.key}>{zh ? s.zh : s.en}</option>)}
        </select>
      </div>
      <div><Lbl>{zh ? "来源" : "Source"}</Lbl>
        <select className={inp} data-testid={`${tid}-source`} value={otherMode ? SOURCE_OTHER : form.leadSource} onChange={(e) => pickSource(e.target.value)} disabled={!form.region}>
          <option value="">{form.region ? (zh ? "选择来源" : "Select") : (zh ? "请先选分公司" : "Pick branch first")}</option>
          {sourceList.map((s) => <option key={s} value={s}>{s}</option>)}
          <option value={SOURCE_OTHER}>{zh ? "其他（手动填写）" : "Other (type manually)"}</option>
          {!otherMode && form.leadSource && !sourceList.includes(form.leadSource) && <option value={form.leadSource}>{form.leadSource}</option>}
        </select>
        {otherMode && (
          <Input data-testid={`${tid}-source-other`} autoFocus value={otherText} onChange={(e) => typeSource(e.target.value)}
            placeholder={zh ? "请填写客户来源，如 朋友介绍 / TikTok" : "Type the source, e.g. friend referral / TikTok"} className="mt-1.5" />
        )}
      </div>
      {REFERRAL_SOURCES[form.leadSource] && (
        <div className="col-span-2">
          <ReferralPicker tid={`${tid}-referral`} zh={zh} type={REFERRAL_SOURCES[form.leadSource]} value={form.referral} onChange={(v) => set("referral", v)} />
        </div>
      )}
      <div><Lbl>{zh ? "项目类型" : "Type"}</Lbl>
        <select className={inp} data-testid={`${tid}-project-type`} value={form.projectType} onChange={(e) => set("projectType", e.target.value)}>
          {(meta?.types || Object.keys(TYPE_LABEL)).map((t) => <option key={t} value={t}>{zh ? (TYPE_LABEL[t]?.zh || t) : (TYPE_LABEL[t]?.en || t)}</option>)}
        </select>
      </div>
      <div><Lbl>{zh ? "预算（万美元）" : "Budget (×$10k)"}</Lbl>
        <Input data-testid={`${tid}-budget`} type="number" value={form.budget} onChange={(e) => set("budget", e.target.value)} placeholder={zh ? "如 25 = $250,000" : "e.g. 25 = $250,000"} />
        {Number(form.budget) > 0 && <p className="text-[10px] text-slate-400 mt-0.5" data-testid={`${tid}-budget-usd`}>= ${(Number(form.budget) * 10000).toLocaleString()}</p>}
      </div>
      <div><Lbl>{zh ? "意向度" : "Temperature"}</Lbl>
        <select className={inp} data-testid={`${tid}-temperature`} value={form.temperature} onChange={(e) => set("temperature", e.target.value)}>
          {Object.keys(TEMP_LABEL).map((t) => <option key={t} value={t}>{zh ? TEMP_LABEL[t].zh : TEMP_LABEL[t].en}</option>)}
        </select>
      </div>
      <div><Lbl>{zh ? "阶段" : "Stage"}</Lbl>
        <select className={inp} data-testid={`${tid}-stage`} value={form.stage} onChange={(e) => set("stage", e.target.value)}>
          {STAGE_ORDER.filter((s) => (s !== "signed" || s === form.stage) && (!["mm_review", "lost"].includes(s) || s === form.stage)).map((s) => <option key={s} value={s}>{zh ? STAGE_LABEL[s].zh : STAGE_LABEL[s].en}</option>)}
        </select>
      </div>
      <div><Lbl>{zh ? "美国销售负责人" : "US Sales Owner"}</Lbl>
        {lockSales ? (
          <>
            <input className={`${inp} bg-slate-50 text-slate-500`} data-testid={`${tid}-assigned`} value={form.assignedSales || selfName} disabled readOnly />
            <p className="text-[10px] text-slate-400 mt-0.5">{zh ? "销售账号默认本人" : "Defaults to yourself"}</p>
          </>
        ) : (
          <>
            <input list={`${tid}-sales-dl`} autoComplete="off" name="owner-us-sales" className={inp} data-testid={`${tid}-assigned`} value={form.assignedSales} onChange={(e) => set("assignedSales", e.target.value)} />
            <datalist id={`${tid}-sales-dl`}>{(owners.usSales || meta?.sales || []).map((s) => <option key={s} value={s} />)}</datalist>
          </>
        )}
      </div>
      <div><Lbl>{zh ? "美国建筑设计师" : "US Architect"}</Lbl>
        <input list={`${tid}-usdesign-dl`} autoComplete="off" name="owner-us-design" className={inp} data-testid={`${tid}-us-design`} value={form.usDesignOwner} onChange={(e) => set("usDesignOwner", e.target.value)} />
        <datalist id={`${tid}-usdesign-dl`}>{(owners.usDesign || []).map((s) => <option key={s} value={s} />)}</datalist>
      </div>
      <div><Lbl>{zh ? "中国建筑设计师" : "CN Architect"}</Lbl>
        <select className={inp} data-testid={`${tid}-cn-drafting`} value={form.cnDraftingOwner} onChange={(e) => set("cnDraftingOwner", e.target.value)}>
          <option value="">{zh ? "待分配（设计阶段再指定）" : "TBD (assigned at design stage)"}</option>
          {(owners.cnDrafting || []).map((s) => <option key={s} value={s}>{s}</option>)}
          {form.cnDraftingOwner && !(owners.cnDrafting || []).includes(form.cnDraftingOwner) && <option value={form.cnDraftingOwner}>{form.cnDraftingOwner}</option>}
        </select>
      </div>
      {form.serviceType === "design_only" && (
        <div className="col-span-2 rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 space-y-2" data-testid={`${tid}-design-quote`}>
          <p className="text-xs font-semibold text-indigo-700">{zh ? "设计报价（仅设计客户）" : "Design Quote (Design-Only)"}</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Lbl>{zh ? "设计单价 $/sqft" : "Rate $/sqft"}</Lbl><Input type="number" data-testid={`${tid}-design-rate`} value={form.designRatePerSqft} onChange={(e) => set("designRatePerSqft", e.target.value)} /></div>
            <div><Lbl>{zh ? "建筑面积 sqft" : "Area sqft"}</Lbl><Input type="number" data-testid={`${tid}-design-area`} value={form.designArea} onChange={(e) => set("designArea", e.target.value)} /></div>
            <div className="col-span-2"><Lbl>{zh ? "手动建筑设计费（依草图/方案）" : "Manual Design Fee (per sketch)"}</Lbl><Input type="number" data-testid={`${tid}-design-manual`} value={form.manualDesignFee} onChange={(e) => set("manualDesignFee", e.target.value)} /></div>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <label className="flex items-center gap-1 cursor-pointer" data-testid={`${tid}-quote-choice-area`}>
              <input type="radio" name={`${tid}-dqc`} checked={form.designQuoteChoice === "area"} onChange={() => set("designQuoteChoice", "area")} />
              <span>{zh ? "按单价×面积" : "Rate × Area"} = <b className="text-indigo-700">${areaQuote.toLocaleString()}</b></span>
            </label>
            <label className="flex items-center gap-1 cursor-pointer" data-testid={`${tid}-quote-choice-manual`}>
              <input type="radio" name={`${tid}-dqc`} checked={form.designQuoteChoice === "manual"} onChange={() => set("designQuoteChoice", "manual")} />
              <span>{zh ? "按手动设计费" : "Manual fee"} = <b className="text-indigo-700">${(Number(form.manualDesignFee) || 0).toLocaleString()}</b></span>
            </label>
            <span className="ml-auto font-semibold text-slate-700" data-testid={`${tid}-quote-final`}>{zh ? "最终设计报价" : "Final quote"}: <span className="text-emerald-600">${finalDesignQuote.toLocaleString()}</span></span>
          </div>
        </div>
      )}
      <div><Lbl>{zh ? "期望完工时间" : "Target completion"}</Lbl><Input data-testid={`${tid}-desired-start`} type="date" value={/^\d{4}-\d{2}-\d{2}$/.test(form.desiredStart || "") ? form.desiredStart : ""} onChange={(e) => set("desiredStart", e.target.value)} />
        {form.desiredStart && !/^\d{4}-\d{2}-\d{2}$/.test(form.desiredStart) && <p className="text-[10px] text-slate-400 mt-0.5" data-testid={`${tid}-desired-start-raw`}>{zh ? `现场调研记录：${form.desiredStart}（选择日期后覆盖）` : `From site survey: ${form.desiredStart} (pick a date to replace)`}</p>}
      </div>
      <div><Lbl>{zh ? "业主首选语言" : "Owner language"}</Lbl>
        <select data-testid={`${tid}-owner-lang`} data-no-i18n className={inp} value={form.ownerLang} onChange={(e) => set("ownerLang", e.target.value)}>
          <option value="zh">中文</option><option value="en">English</option><option value="es">Español</option>
        </select>
        <p className="text-[10px] text-slate-400 mt-0.5">{zh ? "签约转项目后，业主短信 / 邮件 / 门户按此语言" : "Carried to the project: owner SMS / e-mail / portal language"}</p>
      </div>
      <div className="col-span-2"><Lbl>{zh ? "客户画像" : "Customer profile"}</Lbl><AutoGrowTextarea data-testid={`${tid}-primary-concern`} value={form.primaryConcern} onChange={(e) => set("primaryConcern", e.target.value)} placeholder={zh ? "首次沟通要点与客户特征：需求、顾虑、预算 / 时间考虑、决策人、下一步约定…（可多行，框会随内容自动加高）" : "Key points from the first conversation & who the customer is: needs, concerns, budget / timing, decision maker, next steps… (auto-expands)"} /></div>
    </div>
  );
}

/* 其他联系人：姓名 / 关系 / 电话 / 邮箱，可设为主联系人（与表单的 名/姓/电话/邮箱 互换） */
export function LeadContactsEditor({ contacts, setContacts, form, setForm, zh, tid = "edit" }) {
  const addContact = () => setContacts((cs) => [...cs, { name: "", relation: "", phone: "", email: "" }]);
  const upd = (i, k, v) => setContacts((cs) => cs.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const del = (i) => setContacts((cs) => cs.filter((_, j) => j !== i));
  const makePrimary = (i) => {
    const c = contacts[i];
    const oldPrimary = { name: `${form.firstName} ${form.lastName}`.trim(), relation: zh ? "原主联系人" : "Former primary", phone: form.phone, email: form.email };
    const nm = (c.name || "").trim(), sp = nm.lastIndexOf(" ");
    setForm((f) => ({ ...f, firstName: nm ? (sp > 0 ? nm.slice(0, sp) : nm) : f.firstName, lastName: nm ? (sp > 0 ? nm.slice(sp + 1) : "") : f.lastName, phone: c.phone || "", email: c.email || "" }));
    setContacts((cs) => cs.map((x, j) => (j === i ? oldPrimary : x)));
    toast.success(zh ? "已设为主联系人，保存后生效" : "Set as primary — save to apply");
  };
  const ci = "text-sm rounded-lg border border-slate-200 px-2.5 py-1.5 focus:border-indigo-400 focus:outline-none w-full";
  return (
    <div className="border-t border-slate-100 pt-3" data-testid={`${tid}-contacts`}>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-xs font-semibold text-slate-500">{zh ? "其他联系人" : "Additional Contacts"}</label>
        <button type="button" data-testid={`${tid}-add-contact`} onClick={addContact} className="text-[11px] text-indigo-600 hover:text-indigo-700 inline-flex items-center"><Plus className="h-3 w-3 mr-0.5" />{zh ? "添加联系人" : "Add"}</button>
      </div>
      <div className="space-y-2">
        {contacts.map((c, i) => (
          <div key={i} data-testid={`${tid}-contact-${i}`} className="rounded-lg border border-slate-200 p-2 space-y-1.5">
            <div className="flex items-center gap-1.5">
              <input className={ci} placeholder={zh ? "姓名" : "Name"} data-testid={`${tid}-contact-name-${i}`} value={c.name || ""} onChange={(e) => upd(i, "name", e.target.value)} />
              <input className={ci} placeholder={zh ? "关系(业主/配偶/租客)" : "Relation"} data-testid={`${tid}-contact-relation-${i}`} value={c.relation || ""} onChange={(e) => upd(i, "relation", e.target.value)} />
              <button type="button" data-testid={`${tid}-contact-primary-${i}`} onClick={() => makePrimary(i)} title={zh ? "设为主联系人" : "Set primary"} className="shrink-0 text-slate-300 hover:text-amber-500"><Star className="h-4 w-4" /></button>
              <button type="button" data-testid={`${tid}-contact-del-${i}`} onClick={() => del(i)} className="shrink-0 text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
            <div className="flex flex-wrap gap-1">
              {(zh ? ["业主", "配偶", "子女", "父母", "租客", "物业", "代理人"] : ["Owner", "Spouse", "Child", "Parent", "Tenant", "HOA/PM", "Agent"]).map((r) => (
                <button key={r} type="button" data-testid={`${tid}-contact-role-${i}-${r}`} onClick={() => upd(i, "relation", r)}
                  className={`text-[10px] rounded-full px-2 py-0.5 border transition-colors ${c.relation === r ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-200 text-slate-500 hover:border-indigo-300"}`}>{r}</button>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              {/* SYS-001：联系人电话同样自动格式化；每个联系人各存一条，不参与主电话查重 */}
              <input className={ci} placeholder={zh ? "电话 626-227-8888" : "Phone 626-227-8888"} inputMode="tel" data-testid={`${tid}-contact-phone-${i}`} value={c.phone || ""}
                onChange={(e) => upd(i, "phone", formatPhoneInput(e.target.value))} onBlur={(e) => upd(i, "phone", formatPhone(e.target.value))} />
              <input className={ci} placeholder={zh ? "邮箱" : "Email"} data-testid={`${tid}-contact-email-${i}`} value={c.email || ""} onChange={(e) => upd(i, "email", e.target.value)} />
            </div>
          </div>
        ))}
        {contacts.length === 0 && <p className="text-xs text-slate-300 text-center py-2">{zh ? "暂无其他联系人，点击「添加联系人」" : "None — click “Add”"}</p>}
      </div>
    </div>
  );
}
